import React, { useState, useEffect } from "react";
import {
  Dialog,
  TextField,
  CircularProgress,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { updateSlab } from "../../../services/slabs";
import { getAllSlabTypesForMerchant } from "../../../services/slabType";

// Function to detect if dark mode is active from Tailwind's dark class
const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const StyledDialog = styled(Dialog)(({ theme }) => {
  const isDark = isDarkModeActive();
  return {
    "& .MuiBackdrop-root": {
      backdropFilter: "blur(6px)",
      backgroundColor: "rgba(0, 0, 0, 0.2)",
    },
    "& .MuiPaper-root": {
      width: "90%",
      maxWidth: "100%",
      border: `1px solid ${isDark ? "#444" : "#d32f2f"}`,
      borderRadius: 12,
      padding: theme.spacing(2),
      backgroundColor: isDark ? "#1f2937" : theme.palette.background.paper,
      [theme.breakpoints.up("sm")]: { width: "90%" },
      [theme.breakpoints.up("md")]: { width: "500px" },
    },
  };
});

// Custom TextField with Tailwind dark mode support
const CustomTextField = ({ sx, ...props }) => {
  const isDark = isDarkModeActive();
  
  return (
    <TextField
      {...props}
      sx={{
        width: "100%",
        borderRadius: "20px",
        "& .MuiOutlinedInput-root": {
          paddingY: "0 !important",
          height: "46px",
          "& .MuiAutocomplete-input": { padding: "0 !important" },
          "&.Mui-focused fieldset": { borderColor: "#CD0C02 !important" },
          "&:hover fieldset": { borderColor: "#ff6666" },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "#4b5563" : "#e5e7eb",
          },
        },
        "& .MuiInputLabel-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
          "&.Mui-focused": { color: "#CD0C02 !important" },
        },
        "& .MuiInputBase-input": {
          color: isDark ? "#f3f4f6" : "#1f2937",
        },
        "& .MuiFormHelperText-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
        },
        ...sx,
      }}
    />
  );
};

const slabForOptions = [
  { label: "Agent", value: "agent" },
  { label: "Backoffice", value: "backoffice" },
  { label: "Customer", value: "customer" },
];

export default function UpdateSlabModal({ open, onClose, refetch, slabData }) {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state.auth.user);
  const [payload, setPayload] = useState({
    slabTypeId: "",
    percentage: "",
    slabFor: "agent",
  });
  const [errors, setErrors] = useState({});
  const [isDark, setIsDark] = useState(false);

  // Listen for dark mode changes
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    // Create a mutation observer to watch for class changes on html element
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  const { data: slabTypesData } = useQuery({
    queryKey: ["merchantSlabTypes"],
    queryFn: () => getAllSlabTypesForMerchant({ limit: 100 }),
  });

  useEffect(() => {
    if (slabData && open) {
      setPayload({
        slabTypeId: slabData.slabTypeId || "",
        percentage: slabData.percentage || "",
        slabFor: slabData.slabFor || "agent",
      });
    }
  }, [slabData, open]);

  const updateSlabMutation = useMutation({
    mutationFn: ({ id, data }) => updateSlab(id, data),
    onSuccess: () => {
      toast.success(t('updateSlab.success') || "Slab updated successfully");
      refetch();
      resetForm();
      onClose();
    },
    onError: (error) => {
      const message = error?.response?.data?.error || t('updateSlab.error') || "Failed to update slab";
      toast.error(message);
    },
  });

  const resetForm = () => {
    setPayload({
      slabTypeId: "",
      percentage: "",
      slabFor: "agent",
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!payload.slabTypeId) {
      newErrors.slabTypeId = t('updateSlab.errors.slabType') || "Slab type is required";
    }
    
    if (!payload.percentage) {
      newErrors.percentage = t('updateSlab.errors.percentage') || "Percentage is required";
    } else if (isNaN(payload.percentage) || payload.percentage < 0 || payload.percentage > 100) {
      newErrors.percentage = t('updateSlab.errors.percentageRange') || "Percentage must be between 0 and 100";
    }
    
    if (!payload.slabFor) {
      newErrors.slabFor = t('updateSlab.errors.slabFor') || "Slab for is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePayloadChange = (field, value) => {
    setPayload((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleUpdateSlab = async () => {
    if (!validateForm()) return;
    if (!slabData?.id) return;

    await updateSlabMutation.mutateAsync({
      id: slabData.id,
      data: {
        slabTypeId: payload.slabTypeId,
        percentage: parseFloat(payload.percentage),
        slabFor: payload.slabFor,
        isCountry: false,
        countryId: null,
        isProvider: false,
        providerId: null,
      }
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const loading = updateSlabMutation.isPending;

  const currentSlabType = slabTypesData?.data?.find(
    type => type.id === slabData?.slabTypeId
  );

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <div className="px-5 py-3">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            {t('updateSlab.title') || "Update Slab"}
          </h3>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            disabled={loading}
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Current Slab Type Info */}
          <div className={`p-3 rounded-md transition-colors duration-200 ${
            isDark ? 'bg-gray-800' : 'bg-gray-50'
          }`}>
            <Typography 
              variant="body2" 
              className={`mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
            >
              {t('updateSlab.currentSlabType') || "Current Slab Type"}
            </Typography>
            <Typography 
              variant="body1" 
              className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}
            >
              {currentSlabType?.title || slabData?.slabTypeDetails?.title || "N/A"}
            </Typography>
            <Typography 
              variant="caption" 
              className={isDark ? 'text-gray-500' : 'text-gray-400'}
            >
              {t('updateSlab.note') || "Note: Slab type cannot be changed after creation"}
            </Typography>
          </div>

          {/* Percentage Field */}
          <CustomTextField
            fullWidth
            label={t('updateSlab.percentage') || "Percentage"}
            type="number"
            value={parseFloat(payload.percentage).toFixed(2)}
            onChange={(e) => handlePayloadChange("percentage", e.target.value)}
            error={!!errors.percentage}
            helperText={errors.percentage}
            inputProps={{ min: 0, max: 100, step: 0.01 }}
            required
          />

          {/* Created/Updated Info */}
          <div className={`mt-3 p-3 rounded-md transition-colors duration-200 ${
            isDark ? 'bg-blue-900/20' : 'bg-blue-50'
          }`}>
            <Typography 
              variant="body2" 
              className={isDark ? 'text-gray-300' : 'text-gray-600'}
            >
              <strong>{t('updateSlab.created') || "Created"}:</strong> {slabData?.createdAt ? new Date(slabData.createdAt).toLocaleDateString() : "N/A"}
            </Typography>
            {slabData?.updatedAt && (
              <Typography 
                variant="body2" 
                className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mt-1`}
              >
                <strong>{t('updateSlab.lastUpdated') || "Last Updated"}:</strong> {new Date(slabData.updatedAt).toLocaleDateString()}
              </Typography>
            )}
          </div>
        </div>

        <div className="w-full flex justify-end mt-6 gap-3">
          <button
            onClick={handleClose}
            className={`py-2 px-6 border rounded-md transition-colors disabled:opacity-50 ${
              isDark 
                ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                : 'border-gray-400 text-gray-700 hover:bg-gray-50'
            }`}
            disabled={loading}
          >
            {t('common.cancel') || "Cancel"}
          </button>
          <button
            onClick={handleUpdateSlab}
            className={`text-white bg-[#CD0C02] hover:bg-red-700 py-2 px-6 rounded-md disabled:bg-gray-400 flex items-center gap-2 transition-colors ${
              isDark ? 'dark:bg-red-700 dark:hover:bg-red-800 dark:disabled:bg-gray-600' : ''
            }`}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('common.update') || "Update Slab"
            )}
          </button>
        </div>
      </div>
    </StyledDialog>
  );
}